import React from 'react';

// Vertical anchor (bar CENTER, % of portrait height) for the eye-line bar.
// Calibrated to where eyes actually land in the 3:4 center-cropped processed
// portraits (~40%), NOT the design mockup's idealized 31% — the real Phase 0
// crops are geometric/center-based (no face anchoring until Phase 1), so the
// eye-line sits lower than the mockup assumed. Exported so VaultScanGrid (which
// hand-rolls its own bar) stays in lockstep instead of duplicating the number.
export const EYE_LINE_PCT = 40;

// Eye-line score bar overlaid on a portrait. The parent must be
// `position: relative`; the bar anchors its center at EYE_LINE_PCT of the
// portrait height and renders the score in white serif numerals on a
// pure-black, sharp-cornered bar (design-recreation mockup convention —
// serif numerals, not the censor compressed-sans).
//
// Sizes map to the design file's bar heights:
//   xs 24px (archive picker thumbs) · sm 30px (scan strip) · md 44px
//   (vault glowup cards) · lg 52px (comparison panels) · xl 60px (focus scan)
//
// Props:
//   score            string | number — rendered verbatim (e.g. "8.7")
//   size             'xs' | 'sm' | 'md' | 'lg' | 'xl'
//   inset            CSS length for left/right inset (default '8%')
//   showDenominator  render the small "/10" (off for xs by default)
//   lifted           translate the bar up 80px (press-and-hold reveal)
//   ...rest          event handlers (onMouseDown etc.) pass through
const SIZES = {
  xs: { bar: 24, font: 14, denom: null, gap: 3 },
  sm: { bar: 30, font: 18, denom: 9, gap: 4 },
  md: { bar: 44, font: 30, denom: 12, gap: 5 },
  lg: { bar: 52, font: 36, denom: 13, gap: 6 },
  xl: { bar: 60, font: 46, denom: 16, gap: 8 },
};

const PortraitScoreBar = ({
  score,
  size = 'md',
  inset = '8%',
  showDenominator,
  lifted = false,
  className = '',
  style,
  ...rest
}) => {
  const s = SIZES[size] || SIZES.md;
  const denom = showDenominator != null ? showDenominator : s.denom != null;
  return (
    <div
      role="img"
      aria-label={`score: ${score} out of 10`}
      className={['absolute bg-obsidian flex items-baseline justify-center select-none', className]
        .filter(Boolean)
        .join(' ')}
      style={{
        left: inset,
        right: inset,
        top: `calc(${EYE_LINE_PCT}% - ${s.bar / 2}px)`,
        height: s.bar,
        gap: s.gap,
        transform: lifted ? 'translateY(-80px)' : 'translateY(0)',
        transition: 'transform 280ms cubic-bezier(0.65, 0, 0.35, 1)',
        ...style,
      }}
      {...rest}
    >
      <span
        className="font-display font-medium text-bone-pure"
        style={{ fontSize: s.font, lineHeight: `${s.bar}px` }}
      >
        {score}
      </span>
      {denom && (
        <span className="font-display" style={{ fontSize: s.denom || 12, color: '#777' }}>
          /10
        </span>
      )}
    </div>
  );
};

export default PortraitScoreBar;
